import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { developmentApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { Textarea } from "@/components/Textarea";
import { Input } from "@/components/Input";
import { useToast } from "@/context/ToastContext";

interface CreatedDevelopment {
    id: number;
    title: string;
    description: string;
    [key: string]: unknown;
}

interface NewDevelopmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (data: CreatedDevelopment) => void;
}

export const NewDevelopmentModal: React.FC<NewDevelopmentModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showError } = useToast();
    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    // Error highlight refs
    const [titleError, setTitleError] = useState<boolean>(false);
    const [descError, setDescError] = useState<boolean>(false);
    const titleRef = useRef<HTMLDivElement>(null);
    const descRef = useRef<HTMLDivElement>(null);

    const handleSubmit = async (): Promise<void> => {
        const missingTitle = !title.trim();
        const missingDesc = !description.trim();

        if (missingTitle || missingDesc) {
            setTitleError(missingTitle);
            setDescError(missingDesc);

            // Show toast
            showError(t("developments.modal_new.validation_required"));

            // Scroll to the first missing field
            const firstMissing = missingTitle ? titleRef : descRef;
            firstMissing.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            // Clear highlight after 3s
            setTimeout(() => {
                setTitleError(false);
                setDescError(false);
            }, 3000);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                title: title.trim(),
                description: description.trim(),
                priority: "medium",
            };

            const res = await developmentApi.create(payload);

            if (res.result) {
                setTitle("");
                setDescription("");
                onSuccess(res.data as CreatedDevelopment);
                onClose();
            } else {
                showError(
                    res.message || t("developments.modal_new.error_create")
                );
            }
        } catch (err) {
            showError(
                err instanceof Error
                    ? err.message
                    : t("developments.modal_new.error_unexpected")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("developments.modal_new.title")}
            maxWidth="lg"
            onSubmit={handleSubmit}
            submitLabel={t("developments.modal_new.create_button")}
            submittingLabel={t("developments.modal_new.creating")}
            isSubmitting={loading}
            submitIcon={<Icon value="plus" size={15} />}
        >
            {/* Title */}
            <div ref={titleRef}>
                <Input
                    required
                    label={t("developments.modal_new.field_title")}
                    value={title}
                    onChange={(e) => {
                        setTitle(e.target.value);
                        if (titleError) {
                            setTitleError(false);
                        }
                    }}
                    placeholder={t("developments.modal_new.title_placeholder")}
                    className={
                        titleError ? "animate-error-blink rounded-xl" : ""
                    }
                />
            </div>

            {/* Description */}
            <div ref={descRef}>
                <Textarea
                    required
                    rows={4}
                    value={description}
                    onChange={(e) => {
                        setDescription(e.target.value);
                        if (descError) {
                            setDescError(false);
                        }
                    }}
                    label={t("developments.modal_new.field_desc")}
                    placeholder={t("developments.modal_new.desc_placeholder")}
                    fullscreenTitle={t("developments.modal_new.title")}
                    richTextFullscreen
                    className={descError ? "animate-error-blink" : ""}
                />
            </div>
        </Modal>
    );
};
